import { Button, Col, Container, Form, FormControl, InputGroup, Row } from "react-bootstrap";
import { ControlType, FetchStatus, Property } from "../../../base/types";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { useEffect, useRef, useState } from "react";
import { IShuttleBooking, IShuttleReq } from "./properties/shuttleBooking";
import { PageLinkHelper } from "../../../base/helpers";
import { ProgressIndicator, Spinner } from "../../../widgets";
import { Registry } from "../../../base/register"
import { toAbsoluteUrl } from "../../../../helpers";
import { useAuth } from "../../../../modules/auth";
import { useDataContext } from "../../page/context";
import { useGetProperties } from "../../../base/hooks";
import { useGetRoutesQuery, useUpdateShuttleMutation } from "./properties/shuttleBookingApi";
import { useNavigate } from "react-router-dom";
import { ShuttleBookingProperties } from "./properties";
import { useGetCapacity } from "./properties/useGetCapacity"
import moment from "moment";
import { useCreateShuttleBookingMutation } from "./properties/shuttleDetailsApi";

export const ShuttleBookingComponent = (props: IControlAttributes) => {
  const { control } = props;
  const { properties } = useGetProperties({ bindings: control.bindings });
  const navigateToOnBookings: IPageLink | undefined = properties[Property.CancelPageLink];
  const [selectedDate, setSelectedDate] = useState<string>('');
  const { data: routeRecords } = useGetRoutesQuery()
  const [selectedRouteId, setSelectedRouteId] = useState<string>('');
  const [filteredRecords, setFilteredRecords] = useState<IShuttleBooking[]>([]);
  const [data, setShuttleData] = useState<IShuttleBooking[]>([]);
  const headingRef = useRef<HTMLDivElement>(null);
  const [getShuttleRecords] = useUpdateShuttleMutation();
  const [capacity, setCapacity] = useState<number>(0);
  const { user, siteId } = useAuth();
  const { setData } = useDataContext()
  const [insertRecord] = useCreateShuttleBookingMutation();
  const [loadingBookingId, setLoadingBookingId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [searchClicked, setSearchClicked] = useState<boolean>(false);
  const [searchCompleted, setSearchCompleted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { capacities } = useGetCapacity()

  useEffect(() => {
    if (data) {
      setFilteredRecords(data);
    }
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (filteredRecords.length > 0 && headingRef.current) {
      headingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } 
  }, [filteredRecords]);

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 15);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const handleRouteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setSelectedRouteId(selectedId);
  };

  const handleCapacityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCapacity = Number(e.target.value);
    setCapacity(selectedCapacity);
  };

  const handleSearch = () => {
    if (!selectedRouteId || !selectedDate || capacity === 0) {
      return;
    }
    const date = new Date(selectedDate).toISOString().split('T')[0];
    setSearchClicked(true);
    setLoading(true)
    setSearchCompleted(false);
    getShuttleRecords({ routeId: selectedRouteId, date: date, capacity: 0 }).unwrap().then((data) => {
      setShuttleData(data)
    }).finally(() => {
      setLoading(false)
      setSearchCompleted(true);
    });
  };

  const handleBookingClick = (booking: IShuttleBooking) => {
    setLoadingBookingId(booking.id);
    setError('');

    const startTime = moment(booking.starttime, "h:mm A");
    startTime.subtract(5.5, 'hours');
    const updatedStartTime = startTime.format("h:mm A");
    const validTillDate = new Date(booking.validTill || Date.now());
    validTillDate.setDate(validTillDate.getDate() + 1);
    const updatedValidTillDateString = validTillDate.toISOString().split('T')[0];
    const combinedDateAndTime = `${updatedValidTillDateString} ${updatedStartTime}`;

    const record: IShuttleReq = {
      studentId: user?.contactId,
      selectRoute: booking.route,
      selectVehicle: booking.id,
      bookingDateTime: combinedDateAndTime,
      capacity: capacity || 0,
      // edtec365_totalcost1: (capacity || 0) * (booking.amount || 0),
      bookingStatus: 100000006,
    };

    insertRecord(record)
      .unwrap()
      .then(data => {
        setData({ type: "Card", result: data, status: FetchStatus.Success });
        if (siteId && navigateToOnBookings && navigateToOnBookings.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnBookings));
        }
      })
      .catch(err => {
        const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
        setError(message);
      })
      .finally(() => setLoadingBookingId(null));
  };

  return (
    <>
      <div style={{ position: 'relative', height: '49vh', overflow: 'hidden' }}>
        <div style={{ position: 'relative', bottom: 0, width: '100%' }}>
          <img src={toAbsoluteUrl('/media/background-images/busNewBackground - Copy.jpg')} alt="Background" style={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '45vh',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: -1
          }} className="d-lg-block d-none" />
          <img src={toAbsoluteUrl('/media/background-images/Shuttle-Background.jpg')} alt="Background" style={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: '48vh',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: -1
          }} className="d-lg-none" />
        </div>
        <Container className="d-flex flex-column align-items-center" style={{ height: '100%' }}>
          <Row className="mb-2 mt-7">
            <Col>
              <h1>Shuttle Booking</h1>
            </Col>
          </Row>
          <Row className="bg-white p-4 rounded">
            <Col xs={12} md={3} className="mb-3 mb-md-0">
              <InputGroup>
                <InputGroup.Text><i className="bi bi-geo-alt"></i></InputGroup.Text>
                <Form.Select aria-label="Shuttle Type" onChange={handleRouteChange} value={selectedRouteId}>
                  <option value="">Select</option>
                  {routeRecords?.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={3} className="mb-3 mb-md-0">
              <InputGroup>
                <InputGroup.Text><i className="bi bi-people"></i></InputGroup.Text>
                <Form.Select aria-label="Capacity" onChange={handleCapacityChange} value={capacity}>
                  <option value={0}>Passengers</option>
                  {Array.from({ length: capacities?.capicity || 0 }, (_, i) => (
                    <option key={i} value={i + 1}>{i + 1}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={3} className="mb-3 mb-md-0">
              <InputGroup>
                <InputGroup.Text><i className="bi bi-calendar"></i></InputGroup.Text>
                <FormControl
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  aria-label="Date"
                  min={today}
                  max={maxDateStr}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={3} className="d-flex justify-content-between">
              <Button className="col-md-12" variant="danger" disabled={loading} onClick={handleSearch}>
                <span className='indicator-label'>
                  {loading && <Spinner className='me-2' show={loading} />}
                  <span>Search</span>
                </span>
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
      {searchClicked && searchCompleted && (!data || data.length === 0) && (
        <div className="text-center text-danger fs-5 mt-4">
          <p>No Shuttle available</p>
        </div>
      )}
      {data && data.length > 0 && (
        <ShuttleAvailability
          data={data}
          handleBookingClick={handleBookingClick}
          headingRef={headingRef}
          loadingBookingId={loadingBookingId}
          error={error}
          capacity={capacity}
          capacities={capacities}
        />
      )}
    </>
  );
};

const ShuttleAvailability = ({
  data,
  handleBookingClick,
  headingRef,
  loadingBookingId,
  error,
  capacity,
  capacities,
}: {
  data: IShuttleBooking[];
  handleBookingClick: (booking: IShuttleBooking) => void;
  headingRef: React.RefObject<HTMLDivElement>;
  loadingBookingId: string | null;
  error: string;
  capacity: number;
  capacities: any;
}) => {
  const [now, setNow] = useState(moment());
  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 3 * 1000); // every 3s
    return () => clearInterval(interval);
  }, []);
  const sortedData = data.slice().sort((a, b) =>
    moment(a.starttime, "h:mm A").diff(moment(b.starttime, "h:mm A"))
  );
  const shouldDisableBooking = (startTime: string, validTill: string): boolean => {
    const currentDate = now.format("DD-MM-YYYY");
    const isToday = moment(validTill).format("DD-MM-YYYY") === currentDate;
    if (!isToday) return false;
    const currentTime = moment();
    const [hours, minutes] = capacities ? [capacities.hours || 0, capacities.minutes || 0] : [0, 0];
    const timeDifference = moment(startTime, "h:mm A").diff(currentTime, 'minutes');
    return timeDifference < (hours * 60 + minutes);
  };

  const getBookingAmount = (amount?: number) => {
    if (capacity && amount) {
      return capacity * amount;
    }
  }

  return (
    <>
      <div ref={headingRef}>
        <h1 className="text-center mt-5">
          Shuttle Availability ({moment(sortedData[0]?.validTill).format("DD-MM-YYYY")})
        </h1>
      </div>
      {sortedData.map((item, i) => (
        <div className="border rounded p-3 mb-3 bg-light mt-5" key={i}>
          <div className="row">
            <div className="col-7 col-md-2 mb-2">
              <span className="fw-bold">Name</span>
              <div>{item.name}</div>
            </div>
            <div className="col-5 col-md-2 mb-2">
              <span className="fw-bold">Available Seats</span>
              <div>{item.availableSeats}/{item.maxCapicity}</div>

            </div>
            <div className="col-7 col-md-2 mb-2">
              <span className="fw-bold">Start Time</span>
              <div>{moment(item.starttime, ["h:mm A"]).format("HH:mm")}</div>
            </div>
            <div className="col-5 col-md-2 mb-2">
              <span className="fw-bold">Amount Per Seat</span>
              <div>₹{item.amount}</div>
            </div>
            <div className="col-7 col-md-2 mb-2">
              <span className="fw-bold">Amount to Pay</span>
              <div>₹{getBookingAmount(item.amount)}</div>
            </div>
            <div className="col-5 col-md-2 mb-2">
              {item.availableSeats === 0 ? (
                <Button
                  disabled
                  style={{
                    backgroundColor: "grey",
                    color: "white",
                    border: "none"
                  }}
                >
                  Sold Out
                </Button>

              ) : (
                <Button
                  variant="danger"
                  onClick={() => handleBookingClick(item)}
                  disabled={
                    loadingBookingId === item.id ||
                    (item.starttime && item.validTill
                      ? shouldDisableBooking(item.starttime, item.validTill)
                      : false) ||
                    (capacity ?? 0) <= 0 ||
                    (capacity ?? 0) > (item.availableSeats ?? 0)
                  }
                >
                  {loadingBookingId === item.id ? (
                    <ProgressIndicator show={true} message="Please wait..." />
                  ) : (
                    'Book Now'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
      {error && (
        <div className="d-flex justify-content-center alert alert-danger">
          <span className="text-danger">{error}</span>
        </div>
      )}
    </>
  );
}

Registry.addControl({
  id: "a9b25e0a-90a3-4347-8d54-646b4239ea8d",
  name: "Shuttle Booking",
  type: ControlType.ShuttleBooking,
  properties: ShuttleBookingProperties,
  component: ShuttleBookingComponent
})
